import { ALL_LANGUAGES, RELEASE_VERSION } from '@/js/constants';
import helpers from '@/js/helpers';
import Request from './Request';
import EventBus from './event-bus';

let headerAuth = '';
const GIS_LAYER_ID_REGEX =
  /^(?:[^:]+:)?(?<layerId>[a-f0-9]{24})(?:_\d{10})?$/i;

const baseUrl = 'https://besafer.privatecloud.hispatecanalytics.com'; // Endpoint backend
const geoServerWorkspace = 'besafer';

const geoServerProjectWorkSpace = `${baseUrl}/gis/v2/geoserver/${geoServerWorkspace}/wms?`;
const geoServerProjectWorkSpaceInterpol = `${baseUrl}/gis/v2/geoserver/int_results/wms?`;
const geoServerGeneralWmsProxy = `${baseUrl}/gis/v2/geoserver/wms?`;
const geoServerPort = `${baseUrl}/gis/v2/geoserver`;
const geoServerBesafer = `${baseUrl}/gis/v2/geoserver/${geoServerWorkspace}/wms?`;
const geoServerFeatureInfo = `${baseUrl}/gis/v2/feature_info`;
const geoServerSigpacMap = `${baseUrl}/gis/v2/map_image`;

EventBus.$on('newLoggedUser', (payload) => {
  headerAuth = `Bearer ${payload}`;
});

function formatInvoiceDateTime(value) {
  if (!value) return null;

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(trimmedValue)) {
      return trimmedValue;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
      return `${trimmedValue}T00:00:00`;
    }
  }

  const formattedDate = helpers.getDateFormatedForBackEnd(value);
  return formattedDate ? `${formattedDate}T00:00:00` : null;
}

function formatSampleDateTime(value) {
  if (!value) return null;

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    if (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(trimmedValue)
    ) {
      return trimmedValue.length === 16
        ? `${trimmedValue}:00`
        : trimmedValue;
    }
  }

  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return null;
  }

  const year = value.getFullYear();
  const month = `0${value.getMonth() + 1}`.slice(-2);
  const day = `0${value.getDate()}`.slice(-2);
  const hours = `0${value.getHours()}`.slice(-2);
  const minutes = `0${value.getMinutes()}`.slice(-2);
  const seconds = `0${value.getSeconds()}`.slice(-2);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function formatExactDateTime(value) {
  if (!value) return null;

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    if (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(trimmedValue)
    ) {
      return trimmedValue.length === 16
        ? `${trimmedValue}:00`
        : trimmedValue;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
      return `${trimmedValue}T00:00:00`;
    }
  }

  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return null;
  }

  const year = value.getFullYear();
  const month = `0${value.getMonth() + 1}`.slice(-2);
  const day = `0${value.getDate()}`.slice(-2);
  const hours = `0${value.getHours()}`.slice(-2);
  const minutes = `0${value.getMinutes()}`.slice(-2);
  const seconds = `0${value.getSeconds()}`.slice(-2);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function buildSamplingTemplatesHeaders(companyCode, contactCode) {
  return {
    Authorization: headerAuth,
    Company: companyCode,
    Contact: contactCode,
    'app-version': RELEASE_VERSION,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
}

function buildSamplingTemplatesUrl(path, queryParams = {}) {
  const url = new URL(`${baseUrl}${path}`);

  Object.entries(queryParams || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

async function samplingTemplatesRequest(
  path,
  {
    method = 'GET',
    companyCode,
    contactCode,
    queryParams = {},
    body = null,
  } = {}
) {
  if (!companyCode) {
    throw new Error('missing_company_code');
  }

  const url = buildSamplingTemplatesUrl(path, queryParams);
  const options = {
    method,
    headers: buildSamplingTemplatesHeaders(companyCode, contactCode),
  };

  if (body !== null && body !== undefined) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);
  const responseText = await response.text();

  if (!response.ok) {
    const error = new Error(responseText || response.statusText);
    error.status = response.status;
    error.response = responseText;
    throw error;
  }

  return {
    ok: response.ok,
    status: response.status,
    response: responseText || '{}',
  };
}

export default {
  normalizeGISLayerId(value) {
    if (typeof value !== 'string') {
      return value;
    }

    const normalizedValue = value.trim();
    const match = normalizedValue.match(GIS_LAYER_ID_REGEX);

    if (match?.groups?.layerId) {
      return match.groups.layerId;
    }

    return normalizedValue.split(':').pop();
  },
  normalizeWMSOptions(options = {}) {
    const normalizedOptions = { ...options };
    const format =
      normalizedOptions.outputFormat ||
      normalizedOptions.OUTPUTFORMAT ||
      normalizedOptions.format ||
      normalizedOptions.FORMAT;

    delete normalizedOptions.outputFormat;
    delete normalizedOptions.OUTPUTFORMAT;
    delete normalizedOptions.FORMAT;

    if (format) {
      normalizedOptions.format = format;
    }

    return normalizedOptions;
  },
  forceOutputFormat(layer) {
    if (!layer || typeof layer.getTileUrl !== 'function') {
      return layer;
    }

    const originalGetTileUrl = layer.getTileUrl.bind(layer);

    layer.getTileUrl = (...args) => {
      const url = originalGetTileUrl(...args);

      return url
        .replace(/([?&])format=/i, '$1outputFormat=')
        .replace(/([?&])FORMAT=/, '$1outputFormat=');
    };

    return layer;
  },
  createWmsLayer(L, baseUrl, options = {}, ...extraArgs) {
    const layer = L.tileLayer.wms(
      baseUrl,
      this.normalizeWMSOptions(options),
      ...extraArgs
    );

    return this.forceOutputFormat(layer);
  },
  getGeoServerSigpacMap() {
    return geoServerSigpacMap;
  },
  getGeoServerBesafer() {
    return geoServerBesafer;
  },
  getGeoServerWorkspace() {
    return geoServerWorkspace;
  },
  getGeoServerProjectWorkSpace() {
    return geoServerProjectWorkSpace;
  },
  getGeoServerProjectWorkSpaceInterpol() {
    return geoServerProjectWorkSpaceInterpol;
  },
  getGeoServerGeneralWmsProxy() {
    return geoServerGeneralWmsProxy;
  },
  getBaseUrlBackend() {
    return baseUrl;
  },
  getGISProxyBaseUrl() {
    return `${baseUrl}/gis/v2`;
  },
  getGeoServerPort() {
    return geoServerPort;
  },
  getGeoServerFeatureInfo() {
    return geoServerFeatureInfo;
  },
  login(credentials) {
    const url = `${baseUrl}/user/authorization`;
    const payload = {
      data: JSON.stringify({
        email: credentials.username,
        password: credentials.password,
      }),
    };
    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json'
    ).then(this.handleLoginResult);
  },
  getUser(companyId, addressCode) {
    const url = `${baseUrl}/user`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getInsertionToken(rlsUsername, reportId, datasetIds) {
    const url = `${baseUrl}/generate_insertion_token/`;

    const payload = {
      data: JSON.stringify({
        rlsUsername,
        reportId,
        datasetIds,
      }),
    };
    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },

  getOfferList(
    companyCode,
    status = null,
    isOnlineRegister = false
  ) {
    const url = `${baseUrl}/esb/get_listado_ofertas/`;
    const date = new Date();
    const payload = {
      data: JSON.stringify({
        CodCliente: companyCode,
        Estado: status,
        isOnlineRegister,
        FechaFinVigenciaEjecucion:
          helpers.getDateFormatedForBackEnd(date),
      }),
    };
    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },

  getOfferDetails(budgetCode, codIdioma) {
    // const params = new URLSearchParams({
    //   cod_idiomaSubfamilia: codIdioma,
    // });
    const url = `${baseUrl}/esb/get_detalles_ofertas/${budgetCode}/${codIdioma}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getLineDetails(data) {
    const url = `${baseUrl}/esb/get_detalles_lineas/${data.cod_articuloAT}/${data.cod_planta}/${data.idTipoMuestra}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getOfferSummaryDetails(companyCode) {
    const url = `${baseUrl}/esb/get_detalles_ofertas_summary/${companyCode}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getLegislations() {
    const url = `${baseUrl}/esb/get_legislations/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getSamplesType(
    IdFamilia,
    codIdioma,
    subfamilias = [],
    IdSubFamilia = ''
  ) {
    const filter =
      Array.isArray(subfamilias) && subfamilias.length
        ? `?sample_types_filter=${encodeURIComponent(
          JSON.stringify({ IdSubFamilia: subfamilias })
        )}`
        : '';
    const url = `${baseUrl}/esb/get_samples_types/${IdFamilia || ''
      }/${IdSubFamilia || ''}/${codIdioma}/${filter}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getFarmsAndParcels(companyCode) {
    const url = `${baseUrl}/esb/get_farms_parcels/${companyCode}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getAgencies(codPais) {
    const url = `${baseUrl}/esb/get_agencias/${codPais}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getFamilies(codIdioma) {
    const url = `${baseUrl}/esb/get_families/${codIdioma}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getInvoices(companyCode, statusFilter = null) {
    const url = `${baseUrl}/esb/get_albaranes/${companyCode}/${statusFilter || ''
      }`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getInvoiceSamples(companyCode, idInvoice) {
    const url = `${baseUrl}/esb/get_muestras_albaran/${companyCode}/${idInvoice}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  RegisterInvoice(companyCode) {
    const url = `${baseUrl}/esb/alta_albaran/`;
    const date = new Date();
    const payload = {
      data: JSON.stringify({
        idAlbaran: 0,
        CodAlbaran: '',
        CodCliente: companyCode,
        idDestino: 2,
        idAgenciaMensajeria: null,
        AceptadoPorUsuario: false,
        Activo: true,
        TrackingMensajeria: '',
        Penalizacion: 0,
        Total: null,
        RutaAlbaranPDF: '',
        idEstado: 100,
        Observaciones: '',
        FechaRecogida: '',
        FechaAlta: formatExactDateTime(date),
        FechaModificacion: null,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  registerSample(data) {
    const url = `${baseUrl}/esb/alta_muestra/`;
    const date = new Date();
    const payload = {
      data: JSON.stringify({
        CodCliente: data.CodCliente,
        idWMuestra: 0,
        CodMuestra: null,
        DescripcionMuestra: data.DescripcionMuestra,
        idAlbaran: data.idAlbaran,
        CodAlbaran: data.CodAlbaran,
        CodPresupuesto: data.CodPresupuesto,
        CodLinea: data.CodLinea,
        CodArticulo: data.Articulo,
        idFamilia: data.FamiliaTM,
        idSubFamiliaTM: data.idSubFamiliaTM,
        idTipoMuestra:
          data.idTipoMuestra ?? data.Descripcion.idTipoMuestra,
        idSubTipoMuestra: data.SubTipoMuestra,
        LoteCodigo: data.lote,
        Pedido: '',
        idEstado: 101,
        Activo: true,
        idFinca: data.Parcelas,
        FincaLiteral: data.FincaLiteral,
        idParcela: data.Parcela,
        ParcelaLiteral: data.ParcelaLiteral,
        idNivelPunto: data.idNivelPunto || 0,
        NivelPuntoCoordLiteral: data.NivelPuntoCoordLiteral ?? null,
        CultivoOrganico: data.CultivoOrganico,
        LMR: data.lmr,
        CodPais: '',
        CodPais2: '',
        CodPais3: '',
        CodPais4: '',
        CodPais5: '',
        CodPais6: '',
        CodPais7: '',
        CodPais8: '',
        CodPais9: '',
        CodPais10: '',
        Traza: data.trazaCultivo,
        ResumenLMR: false,
        CMA: data.cma,
        idLegislacion: data.Legislacion,
        idMuestreador: 0,
        MuestreadorLiteral:
          data.Sampler ?? data.MuestreadorLiteral ?? null,
        PrecioAT: data.PrecioAT,
        ObservacionesMuestra: data.Observaciones,
        UsuarioAlta: 0,
        FechaMuestreo: formatSampleDateTime(data.SamplingDate),
        FechaAlta: helpers.getDateFormatedForBackEnd(date),
        FechaModificacion: null,
        ArchivoImgRutaLocal: '',
        ArchivoImgNombre: '',
        idAreaServicio: 0,
        PedidoCliente: '',
        idOrigen: 0,
        idPNTMuestreo: 0,
        CloroResidualLibre: data.cloroResidual,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  deleteInvoice(idInvoice, deleteSource = null) {
    const url = `${baseUrl}/esb/borrar_albaran/${idInvoice}/`;

    const payload = {
      data: JSON.stringify({
        deleteSource,
      }),
    };

    return Request.async(
      url,
      payload,
      'PATCH',
      false,
      'application/json',
      headerAuth
    );
  },

  deleteSample(idSample) {
    const url = `${baseUrl}/esb/borrar_muestra/${idSample}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'PATCH',
      false,
      'application/json',
      headerAuth
    );
  },
  updateSample(data) {
    const url = `${baseUrl}/esb/update_muestra/`;
    const date = new Date();
    const pickFirstDefined = (...values) => {
      const match = values.find(
        (value) => value !== undefined && value !== null
      );
      return match ?? null;
    };
    const normalizeSelectionId = (value) => {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const normalizedValue = Number(value);
      return Number.isNaN(normalizedValue) ? null : normalizedValue;
    };
    const normalizedIdFinca =
      normalizeSelectionId(data.idFinca) ??
      normalizeSelectionId(data.IdFinca) ??
      normalizeSelectionId(data.Parcelas);
    const normalizedIdParcela =
      normalizeSelectionId(data.idParcela) ??
      normalizeSelectionId(data.IdParcela) ??
      normalizeSelectionId(data.Parcela);
    const normalizedFincaLiteral =
      data.FincaLiteral ??
      (typeof data.Finca === 'string'
        ? data.Finca
        : typeof data.Parcelas === 'string'
          ? data.Parcelas
          : '');
    const normalizedParcelaLiteral =
      data.ParcelaLiteral ??
      (typeof data.Parcela === 'string' ? data.Parcela : '');
    const resolvedCultivoOrganico = pickFirstDefined(
      data.organicFarming,
      data.CultivoOrganico,
      data.ProduccionOrganica
    );
    const resolvedLmr = pickFirstDefined(data.lmr, data.LMR);
    const resolvedTraza = pickFirstDefined(
      data.trazaCultivo,
      data.Traza
    );
    const resolvedCma = pickFirstDefined(data.cma, data.CMA);
    const resolvedLegislacion = pickFirstDefined(
      data.idLegislacion,
      data.Legislacion
    );
    const resolvedIdNivelPunto =
      normalizeSelectionId(
        pickFirstDefined(data.idNivelPunto, data.IdNivelPunto)
      ) ?? 0;
    const resolvedNivelPuntoCoordLiteral = pickFirstDefined(
      data.NivelPuntoCoordLiteral,
      data.nivelPuntoCoordLiteral
    );
    const payloadData = {
      CodCliente: data.CodCliente,
      idWMuestra: data.IdWMuestra,
      CodMuestra: data.CodMuestra,
      DescripcionMuestra: data.SampleDescription ?? data.Muestra,
      idAlbaran: data.idAlbaran,
      CodAlbaran: data.CodAlbaran ?? '',
      CodPresupuesto: data.CodPresupuesto,
      IdAnalisisTipo: data.IdAnalisisTipo ?? null,
      idAnalisisTipo: data.IdAnalisisTipo ?? null,
      idPresupuestoATNoS:
        data.idPresupuestoATNoS ?? data.IdPresupuestoATNoS ?? null,
      IdPresupuestoATNoS:
        data.IdPresupuestoATNoS ?? data.idPresupuestoATNoS ?? null,
      CodLinea: parseInt(data.CodLinea, 10),
      CodArticulo: data.CodArticulo,
      idFamilia: data.idFamiliaTM,
      idSubFamiliaTM: data.idSubFamiliaTM,
      idTipoMuestra: data.IdTipoMuestra,
      idSubTipoMuestra: data.IdSubTipoMuestra,
      LoteCodigo: data.LoteCodigo ?? data.Lote ?? null,
      Pedido: data.Pedido ?? '',
      idEstado: 101,
      Activo: true,
      idFinca: normalizedIdFinca,
      FincaLiteral: normalizedFincaLiteral,
      idParcela: normalizedIdParcela,
      ParcelaLiteral: normalizedParcelaLiteral,
      idNivelPunto: resolvedIdNivelPunto,
      NivelPuntoCoordLiteral: resolvedNivelPuntoCoordLiteral ?? null,
      CultivoOrganico: resolvedCultivoOrganico,
      LMR: resolvedLmr,
      CodPais: '',
      CodPais2: '',
      CodPais3: '',
      CodPais4: '',
      CodPais5: '',
      CodPais6: '',
      CodPais7: '',
      CodPais8: '',
      CodPais9: '',
      CodPais10: '',
      Traza: resolvedTraza,
      ResumenLMR: false,
      CMA: resolvedCma,
      idLegislacion: resolvedLegislacion,
      idMuestreador: 0,
      MuestreadorLiteral:
        data.Sampler ?? data.MuestreadorLiteral ?? null,
      PrecioAT: data.PrecioAT ?? null,
      ObservacionesMuestra:
        data.Observaciones ?? data.ObservacionesMuestra ?? null,
      UsuarioAlta: 0,
      FechaMuestreo: data.FechaMuestreo,
      FechaAlta: helpers.getDateFormatedForBackEnd(date),
      FechaModificacion: null,
      ArchivoImgRutaLocal: '',
      ArchivoImgNombre: '',
      idAreaServicio: 0,
      PedidoCliente: '',
      idOrigen: 0,
      idPNTMuestreo: 0,
      CloroResidualLibre: data.cloroResidual,
    };

    let payload = {
      data: JSON.stringify(payloadData),
    };

    // Si está actualizando el número de pedido en el apartado final de Registro de muestras
    // el payload tiene estos campos obligatorios, asignando el orden de compra/pedido indicado con el envío
    if (data.checkNumPedido === true) {
      const payloadNumPedido = {
        data: JSON.stringify({
          ...payloadData,
          Pedido: data.orderNumber,
        }),
      };
      payload = payloadNumPedido;
    } else {
      console.log('[Api.updateSample] default payload branch', {
        CodMuestra: data.CodMuestra,
        checkNumPedido: data.checkNumPedido,
        orderNumber: data.Pedido,
        Lote: data.Lote,
        LoteCodigo: data.LoteCodigo,
        PedidoOriginal: data.Pedido,
      });
    }

    return Request.async(
      url,
      payload,
      'PUT',
      false,
      'application/json',
      headerAuth
    );
  },
  updateInvoice(data) {
    const url = `${baseUrl}/esb/update_albaran/`;
    const date = new Date();
    const payload = {
      data: JSON.stringify({
        idAlbaran: data.idAlbaran,
        CodAlbaran: data.CodAlbaran,
        CodCliente: data.CodCliente,
        updateSource: data.updateSource || null,
        idDestino: 2,
        idAgenciaMensajeria: data.Agencia,
        AgenciaLiteral: data.AgenciaLiteral,
        AceptadoPorUsuario: false,
        Activo: true,
        TrackingMensajeria: '',
        Penalizacion: 0.0,
        Total: '',
        RutaAlbaranPDF: '',
        idEstado: 101,
        Observaciones: data.observations,
        FechaRecogida: formatInvoiceDateTime(data.shippingDate),
        FechaAlta: formatExactDateTime(data.FechaAlta || date),
        FechaModificacion: formatExactDateTime(date),
      }),
    };

    return Request.async(
      url,
      payload,
      'PUT',
      false,
      'application/json',
      headerAuth
    );
  },
  acceptOffers(data) {
    const url = `${baseUrl}/esb/aceptar_oferta/`;
    const payload = {
      data: JSON.stringify({
        CodPresupuesto: data.CodPresupuesto,
        Email: data.email,
        Activado: 2,
        IP: data.IP,
        Navegador: data.Navegador,
        Respuesta: 2,
      }),
    };

    return Request.async(
      url,
      payload,
      'PUT',
      false,
      'application/json',
      headerAuth
    );
  },
  invoicePDFGenerator(companyCode, idInvoice, codIdioma) {
    const url = `${baseUrl}/esb/generar_albaran_pdf/`;
    const payload = {
      data: JSON.stringify({
        codCliente: companyCode,
        idAlbaran: idInvoice,
        codIdioma: codIdioma,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  getVideoPeriod(languageValue = null) {
    // Usa el idioma recibido o intenta deducirlo del navegador
    // 'languageValue' puede venir de i18n.locale o del usuario autenticado
    const langValue =
      languageValue || navigator.language.slice(0, 2) || 'es';
    // Buscar equivalencia en ALL_LANGUAGES (ej: 'es' → 'SPA')
    const langMatch = ALL_LANGUAGES.find(
      (l) => l.value === langValue
    );
    const codIdioma = langMatch ? langMatch.key : 'SPA';
    // Construir la URL con el idioma final
    const url = `${baseUrl}/esb/check_video_period/?cod_idioma=${codIdioma}`;
    const payload = { data: null };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getProductionCenterCompanies() {
    const url = `${baseUrl}/esb/get_empresas_centro_produccion/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  get_listado_articulos(CodPlantaVenta) {
    const url = `${baseUrl}/esb/get_listado_articulos/${CodPlantaVenta}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  get_alta_solicitud(data) {
    const url = `${baseUrl}/esb/alta_solicitud/`;
    const fecha = new Date().toISOString();
    const payload = {
      data: JSON.stringify({
        CodPlanta: data.CodPlanta,
        CodSolicitud: '',
        TipoSolicitud: 'SOMB',
        Referencia: data.Referencia,
        FechaSolicitud: fecha,
        CodCliente: data.CodCliente,
        CodDivisa: data.CodDivisa,
        Estado: 1,
        TipoEntrega: data.TipoEntrega,
        CodContacto: data.CodContacto,
        Email: data.Email,
        CodPresupuesto: data.CodPresupuesto,
        CodDireccion: data.CodDireccion,
        Observaciones: data.Observaciones,
        Materiales: data.Materiales,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  get_listado_solicitudes_materiales(
    companyCode,
    queryParams = null
  ) {
    let url = `${baseUrl}/esb/get_listado_solicitudes_materiales/${companyCode}/`;
    if (queryParams && typeof queryParams === 'object') {
      // Note: omit null/empty values to avoid sending "param=null" to the backend.
      const cleaned = {};
      Object.keys(queryParams).forEach((k) => {
        const v = queryParams[k];
        if (v === null || typeof v === 'undefined') return;
        if (typeof v === 'string' && v.trim() === '') return;
        cleaned[k] = v;
      });
      const qs = this.getQueryParams(cleaned);
      if (qs) url += `?${qs}`;
    }

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  get_material_states() {
    const url = `${baseUrl}/esb/material_states/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getCountriesAvailables() {
    const url = `${baseUrl}/get_available_countries/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getAnalysisType(data) {
    const url = `${baseUrl}/esb/get_analisis_tipo/${data.codPlanta}/${data.idFamiliaTM}/${data.idSubFamiliaTM}/${data.codCliente}/${data.codIdioma}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getServicesCatalogFamilies() {
    const url = `${baseUrl}/esb/query_catalog_families/`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getServiceCatalogAnalytics(
    empresa,
    idFamilySelected,
    idTipoMuestra,
    divisa,
    codCliente
  ) {
    const url = `${baseUrl}/esb/query_catalog_analytics/`;
    const payload = {
      data: JSON.stringify({
        empresa,
        idFamiliaTM: idFamilySelected,
        idTipoMuestra,
        divisa,
        codCliente,
      }),
    };
    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  getServicesCatalogParameters(empresa, tipoMuestra, divisa) {
    const url = `${baseUrl}/esb/query_catalog_parameters/`;
    const payload = {
      data: JSON.stringify({
        empresa,
        tipoMuestra,
        divisa,
      }),
    };
    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  getServiceCatalogAnalyticsDetails(data) {
    const url = `${baseUrl}/esb/query_catalog_analytics_details/`;
    const payload = {
      data: JSON.stringify({
        Empresa: data.Empresa,
        TipoMuestra: data.TipoMuestra,
        idAnalisisTipo: data.idAnalisisTipo,
        Divisa: data.Divisa,
      }),
    };
    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  getServiceCatalogNoAnalytics(empresa, divisa) {
    const url = `${baseUrl}/esb/query_catalog_servn/`;
    const payload = {
      data: JSON.stringify({
        empresa,
        divisa,
      }),
    };
    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  getSampleTypesQuery(sampletype, idFamilia) {
    const url = `${baseUrl}/esb/query_sample_type/`;
    const payload = {
      data: JSON.stringify({
        TipoMuestra: sampletype,
        IdFamilia: idFamilia,
      }),
    };
    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  post_copilot_directline_token(companyCode, addressCode, userId) {
    const url = `${baseUrl}/copilot/directline/token/`;
    const payload = {
      data: JSON.stringify({
        userId,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyCode,
      addressCode
    ).then((xhr) => {
      const response = JSON.parse(xhr.response || '{}');
      const requiredFields = [
        'token',
        'conversationId',
        'expires_in',
        'domain',
        'userId',
      ];
      const missingFields = requiredFields.filter(
        (field) =>
          response[field] === undefined ||
          response[field] === null ||
          response[field] === ''
      );

      if (missingFields.length) {
        throw new Error(
          `Invalid Direct Line token response: missing ${missingFields.join(
            ', '
          )}`
        );
      }

      return response;
    });
  },
  // Método que obtiene el listado de incidencias de un cliente por su código
  get_complaints_list(companyCode, codIdioma) {
    const queryParams = new URLSearchParams();

    if (codIdioma) {
      queryParams.set('codIdioma', codIdioma);
    }

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : '';
    const url = `${baseUrl}/esb/get_solicitudes_incidencias/${encodeURIComponent(
      companyCode
    )}/${queryString}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  // Metodo que obtiene el dashboard de incidencias desde el backend local.
  get_complaints_dashboard(companyCode, codIdioma) {
    const queryParams = new URLSearchParams();

    if (codIdioma) {
      queryParams.set('codIdioma', codIdioma);
    }

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : '';
    const url = `${baseUrl}/esb/get_solicitudes_incidencias_dashboard/${encodeURIComponent(
      companyCode
    )}/${queryString}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  // Método que obtiene los detalles de una incidencia por su código
  get_complaint_details(complaintCode, companyCode) {
    const url = `${baseUrl}/esb/get_detalles_incidencia/${complaintCode}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyCode
    );
  },
  // Método que obtiene los archivos adjuntos de una incidencia por su código
  get_complaint_files(complaintCode) {
    const url = `${baseUrl}/esb/get_archivos_adjuntos_incidencia/${complaintCode}/`;

    return fetch(url, {
      method: 'GET',
      headers: {
        Authorization: headerAuth,
        Accept: 'application/json, application/zip',
      },
    }).then(async (res) => {
      // La respuesta puede ser un ZIP o un JSON indicando que no hay archivos
      const contentType = res.headers.get('Content-Type') || '';

      if (contentType.includes('application/json')) {
        const json = await res.json();
        return { type: 'json', data: json };
      }

      if (contentType.includes('application/zip')) {
        const blob = await res.blob();

        // Convertimos blob a base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () =>
            resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        return {
          type: 'zip',
          data: {
            fileBase64: base64,
            contentType,
            filename: `${complaintCode}.zip`,
          },
        };
      }

      throw new Error('Tipo de contenido no esperado');
    });
  },
  // Método que obtiene el informe de contestación de una incidencia por su código
  get_complaint_report(complaintCode) {
    const url = `${baseUrl}/esb/get_informe_contestacion_incidencia/${complaintCode}/`;

    return fetch(url, {
      method: 'GET',
      headers: {
        Authorization: headerAuth,
        Accept: 'application/json, application/zip',
      },
    }).then(async (res) => {
      // La respuesta puede ser un ZIP o un JSON indicando que no hay archivos
      const contentType = res.headers.get('Content-Type') || '';

      if (contentType.includes('application/json')) {
        const json = await res.json();
        return { type: 'json', data: json };
      }

      if (contentType.includes('application/zip')) {
        const blob = await res.blob();

        // Convertimos blob a base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () =>
            resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        return {
          type: 'zip',
          data: {
            fileBase64: base64,
            contentType,
            filename: `${complaintCode}.zip`,
          },
        };
      }

      throw new Error('Tipo de contenido no esperado');
    });
  },
  // Método que obtiene los orígenes de incidencia posibles
  get_complaints_origins(codIdioma) {
    const queryParams = new URLSearchParams();

    if (codIdioma) {
      queryParams.set('codIdioma', codIdioma);
    }

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : '';
    const url = `${baseUrl}/esb/get_origenes_incidencia/${queryString}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  // Método que registra una nueva incidencia y devuelve el código de la misma
  post_new_complaint(payload) {
    const url = `${baseUrl}/esb/post_alta_incidencia/`;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: headerAuth,
      },
      body: JSON.stringify(payload),
    })
      .then((res) => res)
      .catch((err) => err);
  },
  // Método que adjunta archivos a una incidencia ya registrada, indicando el nuevo código
  post_new_complaint_files(payload) {
    const url = `${baseUrl}/esb/post_adjuntar_archivos_incidencia/`;

    // Preparar el objeto como Form Data
    const formData = new FormData();
    formData.append('TituloReclamacion', payload.TituloReclamacion);
    formData.append(
      'ArchivoZip',
      payload.ArchivoZip,
      'archivos_incidencia.zip'
    );

    return fetch(url, {
      method: 'POST',
      headers: {
        Authorization: headerAuth,
      },
      body: formData,
    })
      .then((res) => res)
      .catch((err) => err);
  },
  getHomeSamples(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/${params}`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getAgronomySamples(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/agronomy/${params || ''}`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getAgronomySamplesWithFilters(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/agronomy/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  // Filtrosamples
  getHomeSamplesWithFilters(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSamplesDetailsWithFilters(params, id, companyId, addressCode) {
    const url = `${baseUrl}/samples/${id}/get_parameters/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getEnvironmentSamplesWithFilters(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/environment/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getMiningSamplesWithFilters(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/minery/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getAggregateInfo(companyId, addressCode) {
    const url = `${baseUrl}/samples/aggregated_information/`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getHistoricalAtcs(companyId, addressCode) {
    const url = `${baseUrl}/samples/historical_atcs/`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSampleDetails(id, params, type = '', companyId, addressCode) {
    let url;

    if (type === 'agronomy') {
      url = `${baseUrl}/samples/${id}/get_parameters/`;
    } else {
      url = `${baseUrl}/samples/${id}/get_parameters/${params ?? ''}`;
    }

    return Request.async(
      url,
      {},
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getFoodSamples(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/food/${params || ''}`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getMiningSamples(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/minery/${params || ''}`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getFoodSamplesWithFilters(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/food/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getEnvironmentSamples(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/environment/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  translateLanguage(value) {
    let key = '';
    for (const lang in ALL_LANGUAGES) {
      if (ALL_LANGUAGES[lang].value === value) {
        key = ALL_LANGUAGES[lang].key;
      }
    }

    return key;
  },
  getHealthAndSecuritySamples(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/health_and_security/${params || ''
      }`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getHealthAndSecuritySamplesWithFilters(
    params,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/samples/health_and_security/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getDetalledNutritionalMonitoring(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/get_detalled_nutritional_monitoring/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getPhitomonitoringOptions(params, companyId, addressCode) {
    const url = `${baseUrl}/phitomonitoring_options/`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getPhitomonitoringData(params, companyId, addressCode) {
    const url = `${baseUrl}/phitomonitoring/`;
    const payload = {
      data: params,
    };
    // const payload = {
    //   data: null,
    // };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  createPhitomonitoringData(params, companyId, addressCode) {
    const url = `${baseUrl}/phitomonitoring/`;
    const payload = {
      data: JSON.stringify({ ...params }),
    };
    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  editPhitomonitoringData(params, companyId, addressCode) {
    const url = `${baseUrl}/phitomonitoring/${params.id}/`;
    const payload = {
      data: JSON.stringify({ ...params }),
    };
    return Request.async(
      url,
      payload,
      'PATCH',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  deletePhitomonitoringData(id, companyId, addressCode) {
    const url = `${baseUrl}/phitomonitoring/${id}/`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'DELETE',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  updateUserAddresses(id, addressesData, companyId, addressCode) {
    const url = `${baseUrl}/company/user/${id}/address`;

    const payload = {
      data: { address_code: addressesData },
    };
    return Request.async(
      url,
      payload,
      'PUT',
      true,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  deleteUserAddresses(id, addressesData, companyId, addressCode) {
    const url = `${baseUrl}/company/user/${id}/address`;

    fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: headerAuth,
        company: companyId,
        address: addressCode,
        'app-version': RELEASE_VERSION,
      },
      body: JSON.stringify({ address_code: addressesData }),
    }).catch((err) => console.error(err));
  },
  getSampleReport(sampleIdentifier, docType = 1) {
    const url = `${baseUrl}/etl/get_file/`;
    // El backend de `get_file` ya distingue el tag esperado por `document_type`.
    // Normalizamos el tipo aquí para que todo el frontend use un único mapper:
    // 1/2 -> sample_code, 3 -> study_id, 4 -> parcel_id.
    const normalizedDocType = Number(docType);
    const payloadData = {
      document_type: normalizedDocType.toString(),
    };

    let resolvedSampleCode = null;
    let resolvedSampleId = null;
    let resolvedStudyId = null;
    let resolvedParcelId = null;

    if (
      sampleIdentifier &&
      typeof sampleIdentifier === 'object' &&
      !Array.isArray(sampleIdentifier)
    ) {
      // Seguimos tolerando aliases legacy al leer el identificador de entrada
      // porque algunos componentes/estados aún guardan el mismo valor bajo
      // nombres históricos distintos.
      resolvedSampleCode =
        sampleIdentifier.sampleCode ??
        sampleIdentifier.sample_code ??
        sampleIdentifier.code ??
        sampleIdentifier.codMuestra ??
        sampleIdentifier.CodMuestra ??
        null;
      resolvedSampleId =
        sampleIdentifier.sampleId ??
        sampleIdentifier.sample_id ??
        sampleIdentifier.id_sample ??
        sampleIdentifier.id ??
        null;
      resolvedStudyId =
        sampleIdentifier.studyId ??
        sampleIdentifier.study_id ??
        sampleIdentifier.id_study ??
        null;
      resolvedParcelId =
        sampleIdentifier.parcelId ??
        sampleIdentifier.parcel_id ??
        null;
    } else {
      // Primitive identifier passed (legacy flow).
      resolvedSampleId = sampleIdentifier ?? null;
    }

    if ([1, 2].includes(normalizedDocType)) {
      let sampleCodeValue = null;

      if (
        resolvedSampleCode !== undefined &&
        resolvedSampleCode !== null &&
        String(resolvedSampleCode).trim() !== ''
      ) {
        sampleCodeValue = String(resolvedSampleCode).trim();
      } else if (
        resolvedSampleId !== undefined &&
        resolvedSampleId !== null &&
        String(resolvedSampleId).trim() !== ''
      ) {
        sampleCodeValue = String(resolvedSampleId).trim();
      }

      if (sampleCodeValue) {
        payloadData.sample_code = sampleCodeValue;
      }
    } else if (normalizedDocType === 3) {
      let studyIdValue = null;

      if (
        resolvedStudyId !== undefined &&
        resolvedStudyId !== null &&
        String(resolvedStudyId).trim() !== ''
      ) {
        studyIdValue = String(resolvedStudyId).trim();
      } else if (
        resolvedSampleId !== undefined &&
        resolvedSampleId !== null &&
        String(resolvedSampleId).trim() !== ''
      ) {
        studyIdValue = String(resolvedSampleId).trim();
      }

      if (studyIdValue) {
        // El contrato actual del backend espera `study_id`.
        payloadData.study_id = studyIdValue;
      }
    } else if (normalizedDocType === 4) {
      let parcelIdValue = null;

      if (
        resolvedParcelId !== undefined &&
        resolvedParcelId !== null &&
        String(resolvedParcelId).trim() !== ''
      ) {
        parcelIdValue = String(resolvedParcelId).trim();
      } else if (
        resolvedSampleId !== undefined &&
        resolvedSampleId !== null &&
        String(resolvedSampleId).trim() !== ''
      ) {
        parcelIdValue = String(resolvedSampleId).trim();
      } else if (
        resolvedSampleCode !== undefined &&
        resolvedSampleCode !== null &&
        String(resolvedSampleCode).trim() !== ''
      ) {
        parcelIdValue = String(resolvedSampleCode).trim();
      }

      if (parcelIdValue) {
        // En monitoring el identificador funcional es la parcela, no la muestra.
        payloadData.parcel_id = parcelIdValue;
      }
    }

    const payload = {
      data: JSON.stringify(payloadData),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json'
    );
  },
  uploadFile(params, companyId, addressCode) {
    const url = `${baseUrl}/documentation/`;

    const formData = new FormData();
    formData.append('name', params.name);
    formData.append('contract_number', params.contractNumber);
    formData.append('emails', params.emails.toString());
    formData.append('description', params.description);
    formData.append('attached_file', params.file);
    formData.append('atc', params.atc);
    formData.append('size', params.size);
    formData.append('extension', params.extension);
    formData.append('company_code', companyId);
    formData.append('address_code', addressCode);

    return fetch(url, {
      method: 'POST',
      headers: {
        Authorization: headerAuth,
        company: companyId,
        address: addressCode,
        'app-version': RELEASE_VERSION,
      },
      body: formData,
    })
      .then((res) => res)
      .catch((err) => console.error(err));
  },
  editFile(params, companyId, addressCode) {
    const url = `${baseUrl}/documentation/${params.id}/`;

    const formData = new FormData();

    formData.append('name', params.name);
    formData.append('contract_number', params.contractNumber);
    formData.append('emails', params.emails.toString());
    formData.append('description', params.description);
    formData.append('company_code', companyId);
    formData.append('address_code', addressCode);

    return fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: headerAuth,
        company: companyId,
        address: addressCode,
        'app-version': RELEASE_VERSION,
      },
      body: formData,
    })
      .then((res) => res)
      .catch((err) => console.error(err));
  },
  getDocumentationTree(params, companyId, addressCode) {
    const url = `${baseUrl}/documentation/get_tree/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  deleteFile(id, companyId, addressCode) {
    const url = `${baseUrl}/documentation/${id}/`;

    fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: headerAuth,
        company: companyId,
        address: addressCode,
        'app-version': RELEASE_VERSION,
      },
    })
      .then((res) => res)
      .catch((err) => console.error(err));
  },
  getAttributeList(
    atc,
    attribute,
    sampleId = '',
    farm = '',
    parcel = '',
    valueReport = '',
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/samples/get_attribute_list/`;

    const payload = {
      data: {
        atc,
        attribute,
        sample_id: sampleId,
      },
    };

    if (farm) {
      payload.data.farm = farm;
    }

    if (parcel) {
      payload.data.parcel = parcel;
    }

    if (valueReport) {
      payload.data.value_report = valueReport;
    }

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getAttributeListGeotoolbox(
    atc,
    attribute,
    sampleId = '',
    sampleTypeForGeometry = null,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/samples/get_attribute_list/`;

    const payload = {
      data: {
        atc,
        attribute,
        sample_id: sampleId,
      },
    };

    if (sampleTypeForGeometry !== null) {
      payload.data.sample_type_for_geometry = sampleTypeForGeometry;
    }

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getAttributeListPhytomonitoring(
    atc,
    attribute,
    sampleId = '',
    sampleTypeForGeometry = null,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/phitomonitoring/get_attribute_list/`;

    const payload = {
      data: {
        atc,
        attribute: attribute.attribute,
        category_id: attribute.category_id,
        type_id: attribute.type_id,
        sample_id: sampleId,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSamplesParameterResult(data, companyId, addressCode) {
    const url = `${baseUrl}/samples/get_samples_parameter_result/`;

    const payload = {
      data,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },

  getSamplesFarms(atc, companyId, addressCode) {
    const url = `${baseUrl}/farms/`;
    const payload = {
      data: {
        atc_name: atc,
        company_code: companyId,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSamplesParcels(atc, farm, companyId, addressCode) {
    const url = `${baseUrl}/farms/parcels/`;
    const payload = {
      data: {
        atc_name: atc,
        farm,
        company_code: companyId,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSamplesParametersCmaSuperiorAndRmin(
    data,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/samples/get_samples_parameters_cma_superior_and_rmin/`;

    const payload = {
      data,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSamplesCount(data, companyId, addressCode) {
    const url = `${baseUrl}/samples/get_samples_with_parameter_count/`;

    const payload = {
      data,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSampleInfo(id, companyId, addressCode) {
    const url = `${baseUrl}/samples/${id}/`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSampleInfoByCode(code, companyId, addressCode) {
    const safeCode = encodeURIComponent(code);
    const url = `${baseUrl}/samples/by-code/?code=${safeCode}`;
    const payload = { data: null };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getCmaAptCount(id, companyId, addressCode) {
    const url = `${baseUrl}/samples/${id}/cma_apt_count/`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  updateContact(params, contactCode, companyId, addressCode) {
    const url = `${baseUrl}/contacts/${contactCode}/`;

    return fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: headerAuth,
        company: companyId,
        address: addressCode,
        'app-version': RELEASE_VERSION,
      },
      body: JSON.stringify({
        name: params.name,
        surname: params.surname,
        language: params.language,
        phone: params.phone,
        company_id: params.companyId,
        addresses: params.addresses,
      }),
    }).then(async (res) => {
      if (!res.ok) {
        const error = new Error(res.statusText);
        error.status = res.status;
        error.response = await res.text();
        throw error;
      }

      return res;
    });
  },
  createContact(params, companyId, addressCode) {
    const url = `${baseUrl}/contacts/`;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: headerAuth,
        company: companyId,
        address: addressCode,
        'app-version': RELEASE_VERSION,
      },
      body: JSON.stringify({
        name: params.name,
        surname: params.surname,
        email: params.email,
        language: params.language,
        phone: params.phone,
        company_code: params.companyId,
        addresses: params.addresses,
      }),
    })
      .then((res) => res)
      .catch((err) => err);
  },
  getSampleParameterLMRs(id, companyId, addressCode) {
    const url = `${baseUrl}/samples/${id}/get_sample_parameter_lmrs/`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getEntity(
    entity,
    sampleTypeId,
    countryCode,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/etl/get_data_entity/`;
    const payload = {
      data: JSON.stringify({
        entity,
        sample_type_id: sampleTypeId,
        country_code: countryCode,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  saveParamsAlert(data, companyId, addressCode) {
    const url = `${baseUrl}/parameter_alerts/`;
    const payload = {
      data: JSON.stringify({
        parameters: data.parameters,
        farm: data.farm,
        parcel: data.parcel,
        atc: data.atc,
        sample_type_id: data.sampleTypeId.id,
        sample_type: data.sampleType,
        is_active: true,
        study: data.study,
        sending_notifications: data.sendingNotifications,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  duplicateParamsAlert(data, companyId, addressCode) {
    const url = `${baseUrl}/parameter_alerts/`;
    const payload = {
      data: JSON.stringify({
        parameters: data.parameters,
        farm: data.farm,
        parcel: data.parcel,
        atc: data.atc,
        sample_type_id: data.sampleTypeId,
        sample_type: data.sampleType,
        is_active: true,
        study: data.study,
        sending_notifications: data.sendingNotifications,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getParamsAlerts(params, companyId, addressCode) {
    const url = `${baseUrl}/parameter_alerts/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  updateAlertState(
    id,
    status,
    companyId,
    addressCode,
    isSampleAlert = false
  ) {
    const url = `${baseUrl}${!isSampleAlert ? '/parameter_alerts/' : '/sample_alerts/'
      }${id}/`;
    const payload = {
      data: JSON.stringify({
        is_active: status,
      }),
    };

    return Request.async(
      url,
      payload,
      'PATCH',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  updateParamsAlert(id, data, companyId, addressCode) {
    const url = `${baseUrl}/parameter_alerts/${id}/`;
    const payload = {
      data: JSON.stringify({
        parameters: data.parameters,
        farm: data.farm,
        parcel: data.parcel,
        atc: data.atc,
        sample_type: data.sampleType,
        study: data.study,
        sending_notifications: data.sendingNotifications,
      }),
    };

    return Request.async(
      url,
      payload,
      'PATCH',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  deleteParamsAlert(id, companyId, addressCode) {
    const url = `${baseUrl}/parameter_alerts/${id}/`;
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'DELETE',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  saveSampleAlert(data, companyId, addressCode) {
    let retailerNum;
    if (data.retailers_num !== undefined) {
      retailerNum = data.retailers_num;
    } else {
      retailerNum = data.retailersNum;
    }
    const url = `${baseUrl}/sample_alerts/`;
    const payload = {
      data: JSON.stringify({
        farm: data.farm,
        parcel: data.parcel,
        atc: data.atc,
        sample_type: data.sampleType,
        sample_type_id: data.sampleTypeId,
        sample_name: data.sampleName,
        is_active: data.isActive,
        value_report: data.valueReport,
        n_superior_lmr_parameters: data.nrSuperiorParams,
        study: data.study,
        retailers_num: retailerNum,
        retailers: data.retailers,
        sending_notifications: data.sendingNotifications,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSampleAlerts(params, companyId, addressCode) {
    const url = `${baseUrl}/sample_alerts/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  updateSampleAlert(id, data, companyId, addressCode) {
    const url = `${baseUrl}/sample_alerts/${id}/`;
    const payload = {
      data: JSON.stringify({
        farm: data.farm,
        parcel: data.parcel,
        atc: data.atc,
        sample_type: data.sampleType,
        value_report: data.valueReport,
        n_superior_lmr_parameters: data.nrSuperiorParams,
        retailers_num: data.retailersNum,
        retailers: data.retailers,
        study: data.study,
        sending_notifications: data.sendingNotifications,
      }),
    };

    return Request.async(
      url,
      payload,
      'PATCH',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  deleteSampleAlert(id, companyId, addressCode) {
    const url = `${baseUrl}/sample_alerts/${id}/`;
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'DELETE',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getNotificationsReports(companyId, addressCode, all, params) {
    const url = `${baseUrl}/alert_notifications/${all ? 'all/' : ''}`;
    const payload = {
      data: params,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  deleteNotificationReport(id, companyId, addressCode) {
    const url = `${baseUrl}/alert_notifications/${id}`;
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'DELETE',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getParameterAlertReports(params, companyId, addressCode) {
    const url = `${baseUrl}/parameter_alert_reports/`;
    const payload = {
      data: params,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getParameterAlertReportById(id, companyId, addressCode) {
    const url = `${baseUrl}/parameter_alert_reports/${id}/`;
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSampleAlertReports(params, companyId, addressCode) {
    const url = `${baseUrl}/sample_alert_reports/`;
    const payload = {
      data: params,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSampleAlertReportById(id, companyId, addressCode) {
    const url = `${baseUrl}/sample_alert_reports/${id}/`;
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  deleteParamsAlertReport(id, companyId, addressCode) {
    const url = `${baseUrl}/parameter_alert_reports/${id}/`;
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'DELETE',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  deleteSampleAlertReport(id, companyId, addressCode) {
    const url = `${baseUrl}/sample_alert_reports/${id}/`;
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'DELETE',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getParameterAlertAttributeList(attribute, companyId, addressCode) {
    const url = `${baseUrl}/parameter_alerts/get_attribute_list/`;
    const payload = {
      data: {
        attribute,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getParameterAlertReportAttributeList(
    attribute,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/parameter_alert_reports/get_attribute_list/`;
    const payload = {
      data: {
        attribute,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSampleAlertReportAttributeList(
    attribute,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/sample_alert_reports/get_attribute_list/`;
    const payload = {
      data: {
        attribute,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSampleAlertAttributeList(attribute, companyId, addressCode) {
    const url = `${baseUrl}/sample_alerts/get_attribute_list/`;
    const payload = {
      data: {
        attribute,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getNotificationsDetailAttributeList(
    attribute,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/alert_notifications/get_attribute_list/`;
    const payload = {
      data: {
        attribute,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  setNotificationAsSeen(id, companyId, addressCode) {
    const url = `${baseUrl}/alert_notifications/${id}/seen/`;
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSampleTypesFromSatellite(params, companyId) {
    const url = `${baseUrl}/satellite/sample_type/?company_code=${companyId}`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId
    );
  },
  getEtlSample(companyId, addressCode, sample) {
    const url = `${baseUrl}/etl/get_sample/`;
    const resolvedSampleCode =
      sample?.sample_code ??
      sample?.code ??
      sample?.codMuestra ??
      sample?.CodMuestra ??
      null;
    const resolvedCodLim = sample?.cod_lim ?? sample?.CodLIM ?? null;

    const hasSampleCodeLookup =
      resolvedSampleCode !== undefined &&
      resolvedSampleCode !== null &&
      String(resolvedSampleCode).trim() !== '';

    const hasValidIdSample =
      sample &&
      sample.sample_id !== undefined &&
      sample.sample_id !== null &&
      String(sample.sample_id).trim() !== '' &&
      String(sample.sample_id).trim() !== '0';

    const payloadData = {
      store: true,
    };

    if (hasSampleCodeLookup) {
      payloadData.sample_code = String(resolvedSampleCode).trim();
      if (
        resolvedCodLim !== undefined &&
        resolvedCodLim !== null &&
        String(resolvedCodLim).trim() !== ''
      ) {
        payloadData.cod_lim = String(resolvedCodLim).trim();
      }
    } else if (hasValidIdSample) {
      payloadData.id_sample = sample.sample_id;
    }

    const payload = {
      data: JSON.stringify(payloadData),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getEtlSampleTypes(companyId, addressCode) {
    const url = `${baseUrl}/etl/get_sample_types/`;
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getDestinationCountries(companyId, addressCode, sampleTypeId) {
    let url = `${baseUrl}/etl/get_lmr_countries/?atc_name=Alimentaria`;

    if (sampleTypeId != null) {
      url += `&sample_type_id=${sampleTypeId}`;
    }

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getDataEntityCountries(
    sampleId,
    countryCode,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/etl/get_data_entity/`;
    const payload = {
      data: JSON.stringify({
        entity: 'lmrByTypeSample_Country',
        sample_type_id: sampleId,
        country_code: countryCode,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getFoodAggregatedCalculations(params, companyId, addressCode) {
    let url = `${baseUrl}/samples/food_aggregated_calculations_v2/`;

    if (params.parameters.length > 0) {
      url += `?parameters=${params.parameters.join('&parameters=')}`;
    } else {
      params.parameters = '';
    }

    const payload = {
      data: params,
    };

    if (payload.data.parameters.length > 0) {
      delete payload.data.parameters;
    }

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSatelliteAnalyseType(params, companyId, addressCode) {
    const url = `${baseUrl}/satellite/analyse_type/?company_code=${companyId}`;
    const payload = {
      data: params,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSatelliteSampler(params, companyId, addressCode) {
    const url = `${baseUrl}/satellite/sampler/?company_code=${companyId}`;
    const payload = {
      data: params,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSatelliteThirdClient(params, companyId, addressCode) {
    const url = `${baseUrl}/satellite/third_client/?company_code=${companyId}`;
    const payload = {
      data: params,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSatelliteSampleTypes(params, companyId, addressCode) {
    const url = `${baseUrl}/satellite/sample_type/?company_code=${companyId}`;

    const payload = {
      data: params,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getFarms(withoutGeometries, companyId, addressCode, atc = null) {
    let url = `${baseUrl}/farms/?without_geometry=${withoutGeometries}`;

    if (atc) {
      url += `&atc_name=${atc}`;
    }

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getFarmsWithFilters(companyId, addressCode, params) {
    const url = `${baseUrl}/farms/`;

    const payload = {
      data: params,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getParcels(data, companyId, addressCode) {
    const url = `${baseUrl}/farms/parcels/`;

    const payload = {
      data,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  saveFarmParcelGeoFeature(
    geoFeature,
    childrenGeoFeatures,
    id,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/farms/${id}/`;

    const payload = {
      data: JSON.stringify({
        farm_geofeature: geoFeature,
        children_geofeatures: childrenGeoFeatures,
      }),
    };

    return Request.async(
      url,
      payload,
      'PATCH',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getFarmGeoFeature(id, companyId, addressCode) {
    const url = `${baseUrl}/tree/location`;

    const payload = {
      data: { location: id },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getFarmGeoFeatureByCode(id, companyId, addressCode) {
    const url = `${baseUrl}/tree/location`;

    const payload = {
      data: { code: id },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  postHidrosophStationsForParcels(
    geometries,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/hidrosoph/stations_for_parcels/`;

    const payload = {
      data: JSON.stringify(geometries),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  postFarmStations(id, stations, companyId, addressCode) {
    const url = `${baseUrl}/farms/${id}/`;

    const payload = {
      data: JSON.stringify({
        stations,
      }),
    };

    return Request.async(
      url,
      payload,
      'PATCH',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getFarmsGeometries(companyId, addressCode) {
    const url = `${baseUrl}/farms/has_geometries/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  updateFarmGeoFeature(geoFeature, id, companyId, addressCode) {
    const url = `${baseUrl}/farms/${id}/`;

    const payload = {
      data: JSON.stringify({
        farm_geofeature: geoFeature,
      }),
    };

    return Request.async(
      url,
      payload,
      'PATCH',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getFarmsAttributeList(attribute, companyId, addressCode) {
    const url = `${baseUrl}/farms/get_attribute_list/`;

    const payload = {
      data: { attribute },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getAvaiableDatesForExpertSystem(
    companyId,
    addressCode,
    farmId,
    parcelId
  ) {
    const url = `${baseUrl}/nutritional_profile/get_evaluated/?farm_id=${farmId}&parcel_id=${parcelId}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSamplesWithParametersData(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/get_sample_with_parameters_data/`;
    const payload = { data: params };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getStudySatellite(companyId, addressCode, params) {
    const url = `${baseUrl}/satellite/study/?company_code=${companyId}&address_code=${addressCode}`;
    const payload = { data: params };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  confirmChangePassword(data, companyId, addressCode) {
    const url = `${baseUrl}/user/confirm_password`;

    const formData = new FormData();
    formData.append('password', data.password);
    formData.append('token', data.token);

    return fetch(url, {
      method: 'POST',
      headers: {
        Authorization: headerAuth,
        company: companyId,
        address: addressCode,
        'app-version': RELEASE_VERSION,
      },
      body: formData,
    })
      .then((res) => res)
      .catch((err) => err);
  },
  getSatelliteParameters(atc, sampleType, companyId, addressCode) {
    const url = `${baseUrl}/satellite/parameter/`;
    const payload = {
      data: {
        company_code: companyId,
        sample_type: sampleType,
        atc_name: atc,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSatelliteParametersUnits(
    atc,
    sampleType,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/satellite/parameter_unit/`;
    const payload = {
      data: {
        company_code: companyId,
        sample_type: sampleType.id,
        atc_name: atc,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSatelliteFoodChains(companyId, addressCode) {
    const url = `${baseUrl}/satellite/food_chain_list/`;
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getClimateData(farmId, data, companyId, addressCode) {
    let url = `${baseUrl}/farms/${farmId}/climate_data/?init_date=${data.initDate}&end_date=${data.endDate}&grouped=${data.grouped}&formula=${data.formula}`;
    if (data.parameters.length > 0) {
      url += `&parameters=${data.parameters.join('&parameters=')}`;
    }
    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getWeatherWidgetData(farmId, data, companyId, addressCode) {
    const url = `${baseUrl}/farms/${farmId}/weather_widget_data/?init_date=${data.initDate}&end_date=${data.endDate}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getNutritionalMonitoring(farmId, companyId, addressCode) {
    const url = `${baseUrl}/nutritional_profile/list_for_nutritional_monitoring/?farm_id=${farmId}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getNutritionalMonitoringWithoutGeometry(
    companyId,
    addressCode,
    params = null
  ) {
    const url = `${baseUrl}/nutritional_profile/list_for_nutritional_monitoring/${params || ''
      }`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getLastSampleId(parcelId, companyId, addressCode) {
    const url = `${baseUrl}/samples/get_last_sample_id/?parcel_id=${parcelId}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  saveGeoFeature(data, companyId, addressCode) {
    const url = `${baseUrl}/location`;

    const payload = {
      data: JSON.stringify({
        name: data.name,
        code: data.code,
        coordinates: data.coordinates,
        type: data.type,
        geo_feature: data.geoFeature,
        parent: data.parent,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },

  updateGeoFeature(data, companyId, addressCode) {
    const url = `${baseUrl}/location/${data.id}`;

    const payload = {
      data: JSON.stringify({
        name: data.name,
        code: data.code,
        coordinates: data.coordinates,
        type: data.type,
        geo_feature: data.geoFeature,
        parent: data.parent,
      }),
    };

    return Request.async(
      url,
      payload,
      'PUT',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },

  getTreeLocation(companyId, addressCode) {
    const url = `${baseUrl}/tree/location?access_to_sernv=${true}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSamplesWithGeometry(companyId, addressCode, params) {
    const queryParams = new URLSearchParams();
    queryParams.set('farm_id', params.farm_id);

    if (params.samplingDate_after != null) {
      queryParams.set(
        'samplingDate_after',
        params.samplingDate_after
      );
    }

    if (params.samplingDate_before != null) {
      queryParams.set(
        'samplingDate_before',
        params.samplingDate_before
      );
    }

    if (params.sample_type_family_id != null) {
      queryParams.set(
        'sample_type_family_id',
        params.sample_type_family_id
      );
    }

    if (params.sample_type_id != null) {
      queryParams.set('sample_type_id', params.sample_type_id);
    }

    if (params.parcel_id != null) {
      queryParams.set('parcel_id', params.parcel_id);
    }

    if (params.study != null && params.study !== '') {
      queryParams.set('study', params.study);
    }

    if (params.parameter != null && params.parameter !== '') {
      queryParams.set('parameter_id', params.parameter);
    }

    if (params.atc_name != null && params.atc_name !== '') {
      queryParams.set('atc_name', params.atc_name);
    }

    const url = `${baseUrl}/samples/samples_with_geometry/?${queryParams.toString()}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  fetchTimelineIndexes(
    location,
    initDate,
    endDate,
    index,
    companyId,
    addressCode
  ) {
    const url = `${geoServerIndexStats}`;

    const payload = {
      data: {
        client: geoServerWorkspace,
        location,
        init_date: initDate,
        end_date: endDate,
        index: [index],
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getFeatureInt(props) {
    const queryParams = new URLSearchParams();

    queryParams.set('legend_desc', props.legend_desc);
    queryParams.set('geometry_points', props.geometry_points);
    queryParams.set('geometry_region', props.geometry_region);
    queryParams.set('ref_values', props.ref_values);
    queryParams.set('color_map', props.color_map);
    queryParams.set('name', props.name);

    const url = `${geoServerGetFeatureInt}?${queryParams.toString()}`;

    // const payload = {
    //   data: {
    //     legend_desc: props.legend_desc,
    //     geometry_points: props.geometry_points,
    //     geometry_region: props.geometry_region,
    //     ref_values: props.ref_values,
    //     color_map: props.color_map,
    //     name: props.name,
    //   },
    // };

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
      // companyId,
      // addressCode
    );
  },
  getComparativeGisTable(companyId, addressCode, params = null) {
    const url = `${baseUrl}/farms/comparative_gis_table/`;

    const payload = {
      data: params,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getNutritionalGraphicData(data, companyId, addressCode) {
    const url = `${baseUrl}/samples/nutritional_graphic_data/?date_after=${data.dateAfter}&date_before=${data.dateBefore}&parameters=${data.parameters}&parcel_id=${data.parcelId}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSamplesWithParameterByFarmAndParcel(
    companyId,
    addressCode,
    { parcelId, farmId }
  ) {
    const url = `${baseUrl}/samples/samples_with_parameter_by_farm_and_parcel/?parcel_id=${parcelId}&farm_id=${farmId}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getMirData(dates, companyId, addressCode, farmId) {
    const url = `${baseUrl}/hidrosoph/get_mir_data/?init_date=${dates.initDate}&end_date=${dates.endDate}&farm=${farmId}`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getMirDataStations(dates, companyId, addressCode, stationId) {
    const url = `${baseUrl}/hidrosoph/get_mir_data/?init_date=${dates.initDate}&end_date=${dates.endDate}&source=${stationId}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getLastMirData(companyId, addressCode, farmId) {
    const url = `${baseUrl}/hidrosoph/get_last_mir_data/?farm=${farmId}`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getGisAttributeList(attribute, companyId, addressCode) {
    const url = `${baseUrl}/samples/get_gis_attribute_list/?attribute=${attribute}`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getSamplesReports(params, companyId, addressCode) {
    const url = `${baseUrl}/samples/download_files/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  checkAccessToAdvancedServices(companyId, addressCode) {
    const url = `${baseUrl}/farms/check_access_to_advanced_services/?company_code=${companyId}&address_code=${addressCode}`;
    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getParcelServices(farmId, companyId, addressCode) {
    const url = `${baseUrl}/farms/get_farm_services/`;

    const payload = {
      data: {
        farm_id: farmId,
      },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getStationsList(location, companyId, addressCode) {
    const url = `${baseUrl}/hidrosoph/?location=${location}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getStationsListFarm(farm, companyId, addressCode) {
    const url = `${baseUrl}/hidrosoph/?farm=${farm}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  searchStationByGeometry(geometry, companyId, addressCode) {
    const url = `${baseUrl}/hidrosoph/search_station_by_geometry/`;

    const payload = {
      data: JSON.stringify(geometry),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  saveStations(location, stations, companyId, addressCode) {
    const url = `${baseUrl}/hidrosoph/`;

    const payload = {
      data: JSON.stringify({
        location,
        stations,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getStations(location, companyId, addressCode) {
    const url = `${baseUrl}/hidrosoph/?location=${location}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  updateStations(id, location, stations, companyId, addressCode) {
    const url = `${baseUrl}/hidrosoph/${id}/`;

    const payload = {
      data: JSON.stringify({
        location,
        stations,
      }),
    };

    return Request.async(
      url,
      payload,
      'PUT',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getNutritionalMonitoringWGRAttributeList(
    attribute,
    companyId,
    addressCode
  ) {
    const url = `${baseUrl}/farms/get_parcel_attribute_list/`;

    const payload = {
      data: { attribute },
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getRefValues(data, companyId, addressCode) {
    const url = `${baseUrl}/samples/get_parameter_reference_values/`;

    const payload = {
      data,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getNutritionalMonitoringWithoutGeometryWithFilters(
    companyId,
    addressCode,
    params
  ) {
    const url = `${baseUrl}/nutritional_profile/list_for_nutritional_monitoring/`;
    const payload = {
      data: params,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getNutritionalProfileEvaluation(
    companyId,
    addressCode,
    id,
    params
  ) {
    const url = `${baseUrl}/nutritional_profile_assessment/${id}/`;

    const payload = {
      data: params,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getEntityV2(data, companyId, addressCode) {
    let url = `${baseUrl}/etl/get_sample_type_lmr_by_countries/?sample_type=${data.sampleType}&lmr_origin=${data.lmrOrigin}`;

    if (data.lmrsDestiny.length > 0) {
      url += `&lmr_destiny=${data.lmrsDestiny.join('&lmr_destiny=')}`;
    }
    const payload = {
      data: JSON.stringify({
        sample_type: data.sampleType,
        lmr_origin: data.lmrOrigin,
        lmr_destiny:
          data.lmrsDestiny.length > 0 ? data.lmrsDestiny : [],
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getReceptionAlert(params, companyId, addressCode) {
    const url = `${baseUrl}/reception_alert/${params.contactCode}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  createReceptionAlert(params, companyId, addressCode) {
    const url = `${baseUrl}/reception_alert/`;

    const payload = {
      data: JSON.stringify({
        contact_code: params.contactCode,
      }),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  deleteReceptionAlert(params, companyId, addressCode) {
    const url = `${baseUrl}/reception_alert/${params.contactCode}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'DELETE',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getQueryParams(params) {
    const queryParams = new URLSearchParams();
    for (const param in params) {
      if (Object.hasOwnProperty.call(params, param)) {
        const value = params[param];
        if (typeof value !== 'undefined') {
          queryParams.set(param, value);
        }
      }
    }
    return queryParams.toString();
  },
  getElementsByQueryParams(
    companyId,
    addressCode,
    module,
    queryParams
  ) {
    let url = `${baseUrl}/${module}`;
    url += `?${this.getQueryParams({ ...queryParams })}`;

    const payload = {
      data: null,
    };
    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth,
      companyId,
      addressCode
    );
  },
  getConteoAlbaranes(companyCode) {
    const url = `${baseUrl}/esb/get_conteo_albaranes/${companyCode}`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getIssuedInvoiceList(companyCode, fechaInicio, fechaFin) {
    const url = `${baseUrl}/esb/get_facturas_cliente/${companyCode}/${fechaInicio}/${fechaFin}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getIssuedInvoicePDF(codFactura) {
    const url = `${baseUrl}/esb/get_factura_pdf/${codFactura}/`;
    const payload = { data: null };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getIssuedInvoiceDetails(codFactura) {
    const url = `${baseUrl}/esb/get_detalles_factura/${codFactura}/`;

    const payload = {
      data: null,
    };

    return Request.async(
      url,
      payload,
      'GET',
      false,
      'application/json',
      headerAuth
    );
  },
  getSampleInterpretationAndDiagnosis(
    codMuestra,
    forceNew = false,
    codCliente = '',
    user = '',
    codIdioma = ''
  ) {
    const url = `${baseUrl}/esb/get_interpretacion_y_diagnostico/`;

    const data = {
      codMuestra,
      forceNew: !!forceNew,
      codCliente,
    };

    if (user) {
      data.user = user;
    }
    if (codIdioma) {
      data.codIdioma = codIdioma;
    }

    const payload = {
      data: JSON.stringify(data),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  getSampleLimitsAndCorrections(
    codMuestra,
    forceNew = false,
    codCliente = '',
    user = '',
    codIdioma = ''
  ) {
    const url = `${baseUrl}/esb/get_limites_y_correcciones/`;

    const data = {
      codMuestra,
      forceNew: !!forceNew,
      codCliente,
    };

    if (user) {
      data.user = user;
    }
    if (codIdioma) {
      data.codIdioma = codIdioma;
    }

    const payload = {
      data: JSON.stringify(data),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  getSampleChatbotQuestion(
    codMuestra,
    question,
    user = '',
    codIdioma = ''
  ) {
    const url = `${baseUrl}/esb/get_chatbot_question/`;

    const data = {
      codMuestra,
      question,
    };

    if (user) {
      data.user = user;
    }
    if (codIdioma) {
      data.codIdioma = codIdioma;
    }

    const payload = {
      data: JSON.stringify(data),
    };

    return Request.async(
      url,
      payload,
      'POST',
      false,
      'application/json',
      headerAuth
    );
  },
  getSamplingTemplates(companyCode, contactCode, filters = {}) {
    return samplingTemplatesRequest('/sampling_templates/', {
      method: 'GET',
      companyCode,
      contactCode,
      queryParams: filters,
    });
  },
  getSamplingTemplateById(id, companyCode, contactCode) {
    return samplingTemplatesRequest(`/sampling_templates/${id}/`, {
      method: 'GET',
      companyCode,
      contactCode,
    });
  },
  createSamplingTemplate(data, companyCode, contactCode) {
    return samplingTemplatesRequest('/sampling_templates/', {
      method: 'POST',
      companyCode,
      contactCode,
      body: data,
    });
  },
  updateSamplingTemplate(id, data, companyCode, contactCode) {
    return samplingTemplatesRequest(`/sampling_templates/${id}/`, {
      method: 'PATCH',
      companyCode,
      contactCode,
      body: data,
    });
  },
  deleteSamplingTemplate(id, companyCode, contactCode) {
    return samplingTemplatesRequest(`/sampling_templates/${id}/`, {
      method: 'DELETE',
      companyCode,
      contactCode,
    });
  },
  getDefaultSamplingTemplate(companyCode, contactCode) {
    return samplingTemplatesRequest('/sampling_templates/default/', {
      method: 'GET',
      companyCode,
      contactCode,
    });
  },
  setDefaultSamplingTemplate(id, companyCode, contactCode) {
    return samplingTemplatesRequest(
      `/sampling_templates/${id}/set-default/`,
      {
        method: 'POST',
        companyCode,
        contactCode,
      }
    );
  },
  deactivateSamplingTemplate(id, companyCode, contactCode) {
    return samplingTemplatesRequest(
      `/sampling_templates/${id}/deactivate/`,
      {
        method: 'POST',
        companyCode,
        contactCode,
      }
    );
  },
};
